import { useEffect, useRef, useState } from 'react';
import { STUDIO_PREVIEW } from '../data/studio-preview.generated.ts';
import { useInView, useReducedMotion, useScrambled } from '../motion.ts';

/**
 * Inside the workspace — a question, an answer, and the product.
 *
 * The first version of this put a 1440px screenshot of Studio into a column beside a
 * paragraph, where nothing in it could be read. This one lets the screenshot be what
 * it is — near its own size — and gives the section the film's camera: a glass rail
 * carries the conversation over the left of the stage, and when the answer is a
 * place the camera pushes in on it (the same slow ease the film's Studio section
 * uses), the rest dims, the pointer arrives, and a label names what you are looking
 * at. Then it pulls back and the next question is asked.
 *
 * Still a scripted preview of the *current* Studio with example data, and it says so.
 * `cam` is where the camera goes: `o` is the point it pushes into (a point on the
 * screenshot, in %), `t` where that point ends up on the stage, `z` the zoom. The
 * highlight ring and the pointer live inside the camera layer, so they stay on their
 * pixels while it moves; the label is placed by projecting the ring through the same
 * numbers.
 */
const SCENARIOS = [
  { n: '01', label: 'The journey', question: 'Can we choose where the demo goes next?', answer: 'Yes. Connect your recorded screens, then decide what happens when someone interacts with them.', screen: 'studio-trigger.png', screenName: 'Studio / Flow', action: 'Opening a screen connection', pin: 'Trigger inspector', result: 'Every connection has a purpose.', explanation: 'Choose the trigger, set its conditions, and define the next action in the inspector.', focus: STUDIO_PREVIEW.focus['studio-trigger'], cursor: { x: 86, y: 37 }, cam: { o: [87, 36], t: [-19, 7.5], z: 1.62 }, alt: 'Current Flow Studio with a flow dropdown, connected screens, and the trigger inspector open.' },
  { n: '02', label: 'Your story', question: 'Can we decide which features it shows?', answer: 'You shape the story in Screenplay. Each beat points to a part of your recorded product.', screen: 'studio-screenplay.png', screenName: 'Studio / Screenplay', action: 'Opening the authored demo beats', pin: 'Screenplay beat', result: 'Your story, connected to your screens.', explanation: 'Write the steps and narration for each moment. Your agent demonstrates the story you approve.', focus: STUDIO_PREVIEW.focus['studio-screenplay'], cursor: { x: 49, y: 20 }, cam: { o: [48, 40], t: [18.5, 1.5], z: 1.25 }, alt: 'Current Studio Screenplay showing an example approved beat, its steps, and narration.' },
  { n: '03', label: 'Your team', question: 'Who can make changes to a flow?', answer: 'Share it with readers or writers. Owners manage access to their flow, and admins assign owners.', screen: 'studio-sharing.png', screenName: 'Studio / Sharing', action: 'Opening flow access settings', pin: 'Share dialog', result: 'The right access, for each person.', explanation: 'Manage owner, writer, and reader permissions without leaving the flow you are working on.', focus: STUDIO_PREVIEW.focus['studio-sharing'], cursor: { x: 61, y: 39 }, cam: { o: [50, 44], t: [16.5, -0.5], z: 1.3 }, alt: 'Current Studio Share dialog with example owner, writer, and reader permissions.' },
] as const;

// 0 asking · 1 finding it · 2 camera pushes in · 3 explained · 4 camera pulls back
const HOLD = [0, 1300, 2000, 4400, 1300];
const LOOP_S = 11.4;
const PER_CHAR = 36;
const STATUS = ['A question starts the conversation', 'Finding the relevant moment', '', 'Showing the answer on the product', 'Back to the whole workspace'];

export function Hero() {
  const [scenario, setScenario] = useState(0);
  const [phase, setPhase] = useState(0);
  const [chars, setChars] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const stage = useRef<HTMLDivElement>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const reduced = useReducedMotion();
  const live = useInView(stage, 0.3);
  const running = live && !reduced && !expanded;
  const s = SCENARIOS[scenario]!;
  const p = reduced ? 3 : phase;
  const pushed = p === 2 || p === 3;
  const typedQ = reduced ? s.question : s.question.slice(0, chars);

  // asking: the phase ends when the question has been typed
  useEffect(() => {
    if (!running || phase !== 0) return;
    if (chars >= s.question.length) { const t = setTimeout(() => setPhase(1), 420); return () => clearTimeout(t); }
    const t = setTimeout(() => setChars(c => c + 1), chars === 0 ? 650 : PER_CHAR);
    return () => clearTimeout(t);
  }, [running, phase, chars, s.question.length]);

  // the rest of the loop
  useEffect(() => {
    if (!running || phase === 0) return;
    const t = setTimeout(() => {
      if (phase === 4) { setScenario(n => (n + 1) % SCENARIOS.length); setChars(0); setPhase(0); } else setPhase(x => x + 1);
    }, HOLD[phase]);
    return () => clearTimeout(t);
  }, [running, phase]);

  // choosing a question pulls the camera back first, so it never jumps
  const choose = (i: number) => {
    if (i === scenario) { setChars(0); setPhase(0); return; }
    setPhase(4);
    window.setTimeout(() => { setScenario(i); setChars(0); setPhase(0); }, 900);
  };

  const { o, t, z } = s.cam;
  const f = s.focus;
  // The stage shows the top 780 of the screenshot's 900 px, so a y in the screenshot's % is 900/780 times larger on the stage.
  const sx = o[0] + z * (f.left - o[0]) + t[0], sy = (o[1] + z * (f.top - o[1]) + t[1]) * (STUDIO_PREVIEW.height / 780);   // where the ring's top-left lands on the stage
  const pin = useScrambled(pushed ? s.pin : '', 640);
  const camStyle = { ['--ox' as string]: o[0] + '%', ['--oy' as string]: o[1] + '%', ['--tx' as string]: t[0] + '%', ['--ty' as string]: t[1] + '%', ['--z' as string]: z, ['--iz' as string]: 1 / z };

  return <section className="studio-showcase wrap" aria-labelledby="studio-title">
    <div className="demo-section-intro"><div><p className="eyebrow">INSIDE YOUR WORKSPACE</p><h2 id="studio-title" className="section-title">The experience is theirs.<br/>The controls are yours.</h2></div><p>One workspace for your flows,<br/>your story, and your team.</p></div>

    <div className="showcase" id="studio" ref={stage} data-phase={p} data-running={running}>
      <div className="sc-bar">
        <div className="sc-tabs" role="group" aria-label="Choose a question">
          {SCENARIOS.map((item, i) => (
            <button key={item.label} type="button" aria-pressed={scenario === i} onClick={() => choose(i)}>
              <span className="sc-tab-n">{item.n}</span>{item.label}
              {scenario === i && <i className="sc-tab-time" key={scenario + ':' + (running ? 'r' : 'p')} style={{ animationDuration: LOOP_S + 's', animationPlayState: running ? 'running' : 'paused' }}/>}
            </button>
          ))}
        </div>
        <span className="sc-where"><b>{s.screenName}</b><em>CURRENT STUDIO · EXAMPLE DATA</em></span>
        <button className="expand-button" type="button" onClick={() => { setExpanded(true); dialog.current?.showModal(); }}>Inspect screen ↗</button>
      </div>

      <div className="sc-stage">
        <div className="sc-viewport">
          <div className={'sc-cam' + (pushed ? ' pushed' : '')} style={camStyle}>
            <img className="sc-img" loading="lazy" decoding="async" src="/product/studio-flow.png" width={STUDIO_PREVIEW.width} height={STUDIO_PREVIEW.height} alt="" aria-hidden="true"/>
            {SCENARIOS.map((item, i) => (
              <img key={item.screen} loading="lazy" decoding="async" className={'sc-img sc-img-on' + (i === scenario && p >= 2 ? ' visible' : '')} src={'/product/' + item.screen} width={STUDIO_PREVIEW.width} height={STUDIO_PREVIEW.height}
                alt={i === scenario ? item.alt : ''} aria-hidden={i !== scenario}/>
            ))}
            <div className={'sc-ring' + (pushed ? ' on' : '')} style={{ left: f.left + '%', top: f.top + '%', width: f.width + '%', height: f.height + '%' }} aria-hidden="true"/>
            <span className={'sc-pointer' + (pushed ? ' arriving' : '')} style={{ left: pushed ? s.cursor.x + '%' : '46%', top: pushed ? s.cursor.y + '%' : '70%', ['--iz' as string]: 1 / z }} aria-hidden="true"><svg viewBox="0 0 24 28"><path d="M3 2v21l6-6 5 9 4-2-5-9h9Z"/></svg><span>Persivo</span></span>
          </div>
          <span className={'sc-pin' + (pushed ? ' on' : '')} style={{ left: `clamp(8px, ${sx}%, calc(100% - 190px))`, top: `max(10px, calc(${sy}% - 38px))` }} aria-hidden="true">{pin || ' '}</span>
        </div>

        <aside className="sc-rail" aria-label="The conversation">
          <div className="sc-chat">
            <span className="sc-who">THE BUYER</span>
            <p className="sc-q">“{typedQ}{p === 0 && !reduced && <i className="hs-caret"/>}”</p>
            <div className={'sc-guide' + (p >= 1 ? ' revealed' : '')} aria-hidden={p < 1}><div>
              <div className="sc-guide-id"><img src="/agent-thinking.svg" alt="" className={p === 1 ? 'thinking' : ''}/><span className="sc-who">PERSIVO</span></div>
              <p>{s.answer}</p>
            </div></div>
            <p className="sc-status"><i className={p === 1 ? 'thinking-dots' : 'status-dot'} aria-hidden="true"/>{p === 2 ? s.action : STATUS[p]}</p>
          </div>
          <div className={'sc-take' + (p === 3 ? ' on' : '')}>
            <span className="sc-take-i" aria-hidden="true">↗</span>
            <div><h3>{s.result}</h3><p>{s.explanation}</p></div>
          </div>
        </aside>
      </div>
    </div>

    <div className="hero-footnote"><span>Current Studio interface. Example data. Scripted preview.</span><a className="studio-open-link" href="https://studio.persivo.co">Open Flow Studio <span aria-hidden="true">↗</span></a></div>
    <dialog className="product-lightbox" ref={dialog} aria-label="Inspect the Studio screen" onClose={() => setExpanded(false)} onClick={event => { if (event.target === event.currentTarget) dialog.current?.close(); }}><div className="lightbox-header"><span>{p >= 2 ? s.screenName : 'Studio / Flow'}</span><button autoFocus onClick={() => dialog.current?.close()}>Close ×</button></div><div className="lightbox-image" tabIndex={0} aria-label="Studio screenshot. Scroll to inspect details."><img src={'/product/' + (p >= 2 ? s.screen : 'studio-flow.png')} alt={p >= 2 ? s.alt : 'Current Flow Studio with a single open flow'}/></div><p>Current product interface with example data. This is a scripted preview; select “Meet the agent” for the live demo.</p></dialog>
  </section>;
}
